import { TrashIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { PolicySettings } from 'types/policy';
import { SizeEnum } from 'utils';

export type ReturnPolicyLinksForm = {
  defaultUrl: string;
  storefronts: Array<{ host: string; url: string }>;
};

export const getReturnPolicyLinksForm = (
  links?: PolicySettings['returnPolicyLinks'],
): ReturnPolicyLinksForm => ({
  defaultUrl: links?.defaultUrl || '',
  storefronts: Object.entries(links?.byStorefrontHost || {}).map(
    ([host, url]) => ({ host, url }),
  ),
});

const isSafePolicyUrl = (value: string) => {
  if (value.length > 2048) return false;
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      Boolean(parsed.hostname) &&
      !parsed.username &&
      !parsed.password
    );
  } catch {
    return false;
  }
};

export const getReturnPolicyLinksError = (
  form: ReturnPolicyLinksForm,
): string | null => {
  if (form.defaultUrl.trim() && !isSafePolicyUrl(form.defaultUrl.trim())) {
    return 'Default return policy URL must be a valid HTTPS URL without a username or password.';
  }
  const seen = new Set<string>();
  for (let index = 0; index < form.storefronts.length; index += 1) {
    const entry = form.storefronts[index];
    const host = entry.host.trim().toLowerCase();
    if (
      !/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(
        host,
      )
    ) {
      return `Storefront ${
        index + 1
      } needs a hostname such as example.com, without a URL, port, or path.`;
    }
    if (seen.has(host)) return 'Each storefront hostname must be unique.';
    if (!isSafePolicyUrl(entry.url.trim()))
      return `Storefront ${
        index + 1
      } return policy URL must be a valid HTTPS URL without a username or password.`;
    seen.add(host);
  }
  return null;
};

export const getReturnPolicyLinksPayload = (
  form: ReturnPolicyLinksForm,
): NonNullable<PolicySettings['returnPolicyLinks']> => ({
  ...(form.defaultUrl.trim() ? { defaultUrl: form.defaultUrl.trim() } : {}),
  byStorefrontHost: Object.fromEntries(
    form.storefronts.map(({ host, url }) => [
      host.trim().toLowerCase(),
      url.trim(),
    ]),
  ),
});

export default function ReturnPolicyLinksSettings({
  value,
  onChange,
  error,
}: {
  value: ReturnPolicyLinksForm;
  onChange: (value: ReturnPolicyLinksForm) => void;
  error?: string;
}) {
  return (
    <section className="space-y-5 rounded-lg border border-[#e1e3e5] bg-white p-6">
      <div>
        <h2 className="text-base font-semibold text-[#202223]">
          Storefront return policy links
        </h2>
        <p className="mt-1 text-sm text-[#6d7175]">
          Show the policy for the storefront hosting your embedded returns
          portal. Other storefronts use the default below, then the return
          policy URL above.
        </p>
      </div>
      <label className="block max-w-xl text-sm font-medium text-[#202223]">
        Default return policy URL
        <input
          type="url"
          value={value.defaultUrl}
          placeholder="https://"
          maxLength={2048}
          onChange={(event) =>
            onChange({ ...value, defaultUrl: event.target.value })
          }
          className="mt-2 block w-full rounded-md border border-[#c9cccf] px-3 py-2 text-sm"
        />
      </label>
      {value.storefronts.map((entry, index) => (
        <div
          key={index}
          className="grid items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"
        >
          <label className="block text-xs font-medium text-gray-600">
            Storefront hostname
            <input
              aria-label={`Storefront hostname ${index + 1}`}
              value={entry.host}
              placeholder="example.com"
              maxLength={253}
              onChange={(event) =>
                onChange({
                  ...value,
                  storefronts: value.storefronts.map((row, rowIndex) =>
                    rowIndex === index
                      ? { ...row, host: event.target.value }
                      : row,
                  ),
                })
              }
              className="mt-1 block w-full rounded-md border border-[#c9cccf] px-3 py-2 text-sm text-gray-900"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            Return policy URL
            <input
              aria-label={`Storefront return policy URL ${index + 1}`}
              type="url"
              value={entry.url}
              placeholder="https://"
              maxLength={2048}
              onChange={(event) =>
                onChange({
                  ...value,
                  storefronts: value.storefronts.map((row, rowIndex) =>
                    rowIndex === index
                      ? { ...row, url: event.target.value }
                      : row,
                  ),
                })
              }
              className="mt-1 block w-full rounded-md border border-[#c9cccf] px-3 py-2 text-sm text-gray-900"
            />
          </label>
          <Button
            size={SizeEnum.sm}
            color="none"
            variant="outlined"
            isIconOnly
            props={{
              'aria-label': `Remove storefront policy link ${index + 1}`,
            }}
            onClick={() =>
              onChange({
                ...value,
                storefronts: value.storefronts.filter(
                  (_, rowIndex) => rowIndex !== index,
                ),
              })
            }
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        size={SizeEnum.sm}
        variant="outlined"
        color="none"
        onClick={() =>
          onChange({
            ...value,
            storefronts: [...value.storefronts, { host: '', url: '' }],
          })
        }
      >
        Add storefront policy link
      </Button>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
